<template>
  <div class="container mx-auto px-4 py-8 ">
    <!-- Session Selection -->
    <div class="mb-8">
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-3xl font-bold text-primary">{{ sessionName || 'تقرير الجلسات' }}</h1>
        <div class="flex gap-3">
          <button @click="downloadPDF" class="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark transition-colors">
            <i class="fas fa-download ml-2"></i>
            تصدير PDF
          </button>
        </div>
      </div>
      <div class="flex justify-center gap-4 items-center">
        <input
          type="date"
          v-model="selectedDate"
          @change="handleSessionChange"
          class="block px-4 py-2 border-2 border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <select 
          v-model="selectedSessionId"
          @change="handleSessionChange"
          class="block w-64 px-4 py-2 bg-white border-2 border-primary rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary">
          <option value="">اختر الجلسة</option>
          <option v-for="session in sessions" :key="session.id" :value="session.id">
            {{ session.sessionsName }}
          </option>
        </select>
        <select
          v-model="selectedOrganizationId"
          class="block w-64 px-4 py-2 bg-white border-2 border-primary rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="">اختر الجهة</option>
          <option v-for="org in organizations" :key="org.id" :value="org.id">
            {{ org.name }}
          </option>
        </select>
      </div>
    </div>

    <!-- Teacher Availability Status -->
    <div v-if="teacherAvailability !== null" class="mb-6 p-4 rounded-lg" :class="teacherAvailability ? 'bg-green-100 border-green-400 text-green-700' : 'bg-red-100 border-red-400 text-red-700'">
      <div class="flex items-center">
        <i :class="teacherAvailability ? 'fas fa-check-circle text-green-500' : 'fas fa-times-circle text-red-500'" class="mr-2"></i>
        <span class="font-semibold">
          {{ teacherAvailability ? 'المعلم متوفر في هذا اليوم' : 'المعلم غير متوفر في هذا اليوم' }}
        </span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-8">
      <div class="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      <p class="mt-4 text-lg text-primary">جاري التحميل...</p>
    </div>

    <!-- Error State -->
    <div v-if="error" class="bg-red-100 border-2 border-red-400 text-red-700 px-6 py-4 rounded-lg relative text-right mb-4">
      <strong class="font-bold ml-2">خطأ!</strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>

    <!-- Success Message -->
    <div v-if="successMessage" class="bg-green-100 border-2 border-green-400 text-green-700 px-6 py-4 rounded-lg relative text-right mb-4">
      <strong class="font-bold ml-2">نجح!</strong>
      <span class="block sm:inline">{{ successMessage }}</span>
    </div>

    <!-- Report Content -->
    <div v-if="selectedSessionId && !loading" class="session-report">
      <!-- PDF Header (hidden on screen, visible in PDF) -->
      <div class="pdf-header">
        <div class="header-container">
          <div class="logo-left">
            <img :src="appLogo" alt="شعار التطبيق" crossorigin="anonymous" />
          </div>
          <div class="header-center">
            <h2 class="report-title">تقرير المواعيد</h2>
            <div class="report-info">
              <div class="info-row">
                <span class="info-label">التاريخ:</span>
                <span class="info-value">{{ formattedDate }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">الجلسة:</span>
                <span class="info-value">{{ sessionName }}</span>
              </div>
            </div>
          </div>
          <div class="logo-right">
            <img v-if="selectedOrganization && selectedOrganization.logoUrl"
                 :src="selectedOrganization.logoUrl"
                 :alt="selectedOrganization.name"
                 crossorigin="anonymous"
                 @error="onOrgLogoError" />
            <img v-else-if="selectedOrganization && selectedOrganization.logoBase64"
                 :src="`data:image/jpeg;base64,${selectedOrganization.logoBase64}`"
                 :alt="selectedOrganization.name"
                 crossorigin="anonymous"
                 @error="onOrgLogoError" />
            <div v-else-if="selectedOrganization" class="logo-placeholder">
              <i class="fas fa-building"></i>
              <span class="placeholder-text">{{ selectedOrganization.name.charAt(0) }}</span>
            </div>
            <div v-else class="logo-placeholder">
              <i class="fas fa-building"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Unified Sessions Table (booked + available) -->
      <div v-if="tableRows.length" class="session-block mb-8">
        <h3 class="text-2xl font-bold mb-6 text-primary border-b-2 border-secondary pb-2">جميع الفترات</h3>
        <div class="overflow-x-auto">
          <table class="min-h-full bg-white rounded-lg shadow overflow-hidden text-center table-fixed w-full">
            <thead class="bg-secondary text-white">
              <tr>
                <th class="px-4 py-2">رقم الجلسة</th>
                <th class="px-4 py-2">الصلاة</th>
                <th class="px-4 py-2">الدارس</th>
                <th class="px-4 py-2">الطالب البديل</th>
                <th class="px-4 py-2">الوقت</th>
                <th class="px-4 py-2 actions-col">الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in tableRows"
                :key="row.isAvailable ? `avail-${row.prayer}-${row.time}` : `${row.prayer}-${row.studentId}-${row.time}`"
                :class="[
                  'border-b last:border-0',
                  row.isAvailable ? 'bg-green-50' : '',
                  row.isCancelled ? 'bg-orange-50' : ''
                ]"
              >
                <td class="px-4 py-2 font-semibold">
                  {{ row.sessionNumber || '-' }}
                </td>
                <td class="px-4 py-2">{{ PRAYER_NAME_MAP[row.prayer] || row.prayer }}</td>
                <td
                  class="px-4 py-2"
                  :class="[
                    row.isAvailable ? 'text-green-700 font-semibold' : '',
                    row.isCancelled ? 'text-orange-700 font-semibold' : ''
                  ]"
                >
                  {{ row.studentName }}
                </td>
                <td class="px-4 py-2 text-blue-700 font-semibold">
                  {{ row.replacementStudentName || '-' }}
                </td>
                <td class="px-4 py-2 font-bold">
                  {{ formatTime(row.time) }}
                </td>
                <td class="px-4 py-2 actions-col">
                  <div v-if="!row.isAvailable && !row.isCancelled" class="flex justify-center gap-2">
                    <button @click="editSession(row)" class="px-3 py-1 bg-secondary text-white rounded hover:bg-secondary-dark transition-colors text-sm">
                      <i class="fas fa-edit"></i> تعديل
                    </button>
                    <button @click="deleteSession(row)" class="px-3 py-1 bg-error text-white rounded hover:bg-error-dark transition-colors text-sm">
                      <i class="fas fa-trash"></i> حذف
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-else-if="!loading && !error" class="text-center py-12 text-gray-500 text-lg">
      الرجاء اختيار جلسة وتاريخ لعرض التقرير.
    </div>

    <!-- Edit Session Modal -->
    <div v-if="showEditModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
        <h3 class="text-xl font-bold mb-4 text-primary">تعديل الجلسة</h3>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">الدارس</label>
          <p class="text-gray-600">{{ editingSession?.studentName }}</p>
        </div>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">الوقت الجديد</label>
          <select
            v-model="editingSession.newTime"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="" disabled>اختر وقتاً</option>
            <option
              v-for="slot in availableSessionTimes"
              :key="slot.id"
              :value="slot.startEn"
            >
              {{ slot.prayerName }} - {{ formatTime(slot.startEn) }}
            </option>
          </select>
        </div>
        <div class="flex justify-end gap-3">
          <button
            @click="cancelEdit"
            class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors"
          >
            إلغاء
          </button>
          <button
            @click="saveEdit"
            class="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark transition-colors"
            :disabled="!editingSession?.newTime"
          >
            حفظ
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
        <h3 class="text-xl font-bold mb-4 text-red-600">تأكيد الحذف</h3>
        <p class="mb-6 text-gray-700">
          هل أنت متأكد من حذف جلسة الدارس <strong>{{ deletingSession?.studentName }}</strong> 
          في وقت <strong>{{ formatTime(deletingSession?.time) }}</strong>؟
        </p>
        <div class="flex justify-end gap-3">
          <button
            @click="cancelDelete"
            class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors"
          >
            إلغاء
          </button>
          <button
            @click="confirmDelete"
            class="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
          >
            حذف
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import url from '@/url';
import html2pdf from 'html2pdf.js';
import appLogo from '@/assets/logo.png';

export default {
  name: 'SessionReport',
  data() {
    return {
      sessions: [],
      organizations: [],
      selectedSessionId: '',
      selectedOrganizationId: '',
      selectedDate: new Date().toISOString().split('T')[0],
      sessionName: '',
      morningSession: {},
      afternoonSession: {},
      eveningSession: {},
      loading: false,
      error: null,
      teacherAvailability: null,
      successMessage: '',
      showEditModal: false,
      showDeleteModal: false,
      editingSession: null,
      deletingSession: null,
      appLogo: appLogo,
      availableSessionTimes: []
    };
  },
  methods: {
    formatTime(time) {
      if (!time) return '';
      
      // Parse the time string and format it directly without timezone conversion
      const [hours, minutes] = time.split(':');
      const date = new Date();
      date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      
      // Format in Arabic with 12-hour format
      return date.toLocaleTimeString('ar-SA', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    },
    async fetchSessions() {
      try {
        const response = await axios.get(process.env.VUE_APP_SERVICE_URL + url.master_sessions, {
          headers: {
            'X-Authorization': localStorage.getItem('token')
          }
        });
        if (response.data?.result?.data) {
          this.sessions = response.data.result.data;
        }
      } catch (err) {
        this.error = err.response?.data?.mesAr || 'حدث خطأ أثناء تحميل الجلسات';
        console.error('Error fetching sessions:', err);
      }
    },
    async fetchOrganizations() {
      try {
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.organizations,
          { headers: { 'X-Authorization': localStorage.getItem('token') } }
        );
        if (response.data?.result?.data) this.organizations = response.data.result.data;
      } catch (err) {
        console.error('Error fetching organizations:', err);
      }
    },
    async handleSessionChange() {
      this.clearMessages();
      if (!this.selectedSessionId) {
        this.clearReport();
        return;
      }
      await this.fetchReport();
    },
    clearReport() {
      this.sessionName = '';
      this.morningSession = {};
      this.afternoonSession = {};
      this.eveningSession = {};
      this.error = null;
      this.availableSessionTimes = [];
    },
    
    /**
     * Validate the received session data to ensure time fields are correct
     */
    validateSessionData() {
      if (!this.availableSessionTimes || this.availableSessionTimes.length === 0) {
        console.log('No available session times to validate');
        return;
      }
      
      console.log('=== VALIDATING SESSION DATA ===');
      this.availableSessionTimes.forEach((slot, index) => {
        console.log(`Slot ${index + 1}:`);
        console.log(`  ID: ${slot.id}`);
        console.log(`  startEn: ${slot.startEn} (should be start time)`);
        console.log(`  endEn: ${slot.endEn} (should be end time)`);
        console.log(`  prayerName: ${slot.prayerName}`);
        
        // Validate time format
        if (slot.startEn && !slot.startEn.includes(':')) {
          console.error(`  ❌ ERROR: startEn has invalid format: ${slot.startEn}`);
        }
        if (slot.endEn && !slot.endEn.includes(':')) {
          console.error(`  ❌ ERROR: endEn has invalid format: ${slot.endEn}`);
        }
        
        // Validate that start time is before end time
        if (slot.startEn && slot.endEn) {
          const startTime = slot.startEn;
          const endTime = slot.endEn;
          if (startTime >= endTime) {
            console.warn(`  ⚠️ WARNING: start time (${startTime}) is not before end time (${endTime})`);
          }
        }
      });
      console.log('=== VALIDATION COMPLETE ===');
    },
    
    async fetchReport() {
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL +
            url.students_sessions_report +
            this.selectedSessionId +
            (this.selectedDate ? `?date=${this.selectedDate}` : ''),
          {
            headers: {
              'X-Authorization': localStorage.getItem('token')
            }
          }
        );
        if (response.data?.result?.data) {
          const reportData = response.data.result.data;
          this.sessionName = reportData.sessionName;
          this.morningSession = reportData.morningSession;
          this.afternoonSession = reportData.afternoonSession;
          this.eveningSession = reportData.eveningSession;
          this.availableSessionTimes = reportData.availableSessionTimes || [];
          
          // Debug logging to see what values are received
          console.log('Available session times received:', this.availableSessionTimes);
          if (this.availableSessionTimes.length > 0) {
            console.log('First available session time:', this.availableSessionTimes[0]);
            console.log('startEn:', this.availableSessionTimes[0].startEn);
            console.log('endEn:', this.availableSessionTimes[0].endEn);
          }
          
          // Validate the received data
          this.validateSessionData();
        }
      } catch (err) {
        this.error = err.response?.data?.mesAr || 'حدث خطأ أثناء تحميل التقرير';
        console.error('Error fetching report:', err);
      } finally {
        this.loading = false;
      }
    },
    async downloadPDF() {
      try {
        // Show PDF-only header
        const pdfHeader = document.querySelector('.pdf-header');
        if (pdfHeader) pdfHeader.style.display = 'block';

        // Wait for header to render
        await new Promise(res => setTimeout(res, 200));

        const element = document.querySelector('.session-report');
        element.classList.add('pdf-export');

        // Calculate dynamic PDF height to fit all data
        const pxToMm = px => px * 0.264583;
        const contentHeightPx = element.scrollHeight;
        const contentHeightMm = pxToMm(contentHeightPx) + 5; // add a small margin

        // Generate filename
        // eslint-disable-next-line no-useless-escape
        const baseName = `تقرير_المواعيد_${this.sessionName}_${this.selectedDate}`.replace(/[\s\/]/g, '_');

        // Options for multi-page PDF
        const opt = {
          margin: [0, 0, 0, 0],
          filename: `${baseName}.pdf`,
          image: { type: 'jpeg', quality: 1.0, crossOrigin: 'anonymous' },
          html2canvas: {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
          },
          jsPDF: { unit: 'mm', format: [210, contentHeightMm], orientation: 'portrait' }, // 210mm = A4 width
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
        };

        await html2pdf().set(opt).from(element).save();

        // Cleanup
        element.classList.remove('pdf-export');
        if (pdfHeader) pdfHeader.style.display = 'none';
      } catch (error) {
        console.error('Error generating PDF:', error);
        this.error = 'حدث خطأ أثناء تحميل الملف';
        // Hide PDF-only header on error
        const pdfHeader = document.querySelector('.pdf-header');
        if (pdfHeader) pdfHeader.style.display = 'none';
      }
    },
    async checkTeacherAvailability() {
      try {
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL +
            url.teacher_availability +
            this.selectedSessionId +
            (this.selectedDate ? `?date=${this.selectedDate}` : ''),
          {
            headers: {
              'X-Authorization': localStorage.getItem('token')
            }
          }
        );
        if (response.data?.result?.data) {
          this.teacherAvailability = response.data.result.data.isAvailable;
        }
      } catch (err) {
        this.error = err.response?.data?.mesAr || 'حدث خطأ أثناء فحص توفر المعلم';
        console.error('Error checking teacher availability:', err);
      }
    },
    editSession(slot) {
      this.editingSession = { 
        ...slot, 
        newTime: slot.time // Initialize with current time
      };
      this.showEditModal = true;
    },
    cancelEdit() {
      this.editingSession = null;
      this.showEditModal = false;
    },
    async saveEdit() {
      try {
        this.loading = true;
        const response = await axios.put(
          process.env.VUE_APP_SERVICE_URL + 
          url.edit_session + 
          this.selectedSessionId, // Use selectedSessionId instead of editingSession.sessionId
          {
            newTime: this.editingSession.newTime, // Use newTime (the edited time) instead of time
            date: this.selectedDate,
            studentId: this.editingSession.studentId // This ensures only this student's session is edited
          },
          {
            headers: {
              'X-Authorization': localStorage.getItem('token')
            }
          }
        );
        if (response.data?.result?.status === 200) {
          this.successMessage = 'تم تحديث الجلسة بنجاح';
          this.showEditModal = false;
          this.editingSession = null;
          await this.fetchReport(); // Refresh the report
        }
      } catch (error) {
        console.error('Error editing session:', error);
        this.error = 'حدث خطأ أثناء تحديث الجلسة';
      } finally {
        this.loading = false;
      }
    },
    deleteSession(slot) {
      this.deletingSession = slot;
      this.showDeleteModal = true;
    },
    cancelDelete() {
      this.deletingSession = null;
      this.showDeleteModal = false;
    },
    async confirmDelete() {
      try {
        this.loading = true;
        const response = await axios.delete(
          process.env.VUE_APP_SERVICE_URL + 
          url.delete_session + 
          this.selectedSessionId + // Use selectedSessionId instead of deletingSession.sessionId
          `?date=${this.selectedDate}&studentId=${this.deletingSession.studentId}`,
          {
            headers: {
              'X-Authorization': localStorage.getItem('token')
            }
          }
        );
        if (response.data?.result?.status === 200) {
          this.successMessage = 'تم حذف الجلسة بنجاح';
          this.showDeleteModal = false;
          this.deletingSession = null;
          await this.fetchReport(); // Refresh the report
        }
      } catch (error) {
        console.error('Error deleting session:', error);
        this.error = 'حدث خطأ أثناء حذف الجلسة';
      } finally {
        this.loading = false;
      }
    },
    // Clear messages when changing sessions
    clearMessages() {
      this.error = null;
      this.successMessage = '';
      this.teacherAvailability = null;
    },
    onOrgLogoError(event) {
      event.target.style.display = 'none';
    },
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('ar-SA', { year: 'numeric', month: '2-digit', day: '2-digit' });
    }
  },
  created() {
    this.fetchSessions();
    this.fetchOrganizations();
  },
  computed: {
    PRAYER_NAME_MAP() {
      return {
        FAJR: 'الفجر',
        DHUHR: 'الظهر',
        ASR: 'العصر',
        MAGHRIB: 'المغرب',
        ISHA: 'العشاء'
      };
    },
    selectedOrganization() {
      return this.organizations.find(o => o.id === this.selectedOrganizationId);
    },
    formattedDate() {
      if (!this.selectedDate) return '';
      const d = new Date(this.selectedDate);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    combinedSessions() {
      return [
        ...Object.entries(this.morningSession).flatMap(([prayer, slots]) => slots.map(slot => ({ ...slot, prayer }))),
        ...Object.entries(this.afternoonSession).flatMap(([prayer, slots]) => slots.map(slot => ({ ...slot, prayer }))),
        ...Object.entries(this.eveningSession).flatMap(([prayer, slots]) => slots.map(slot => ({ ...slot, prayer })))
      ];
    },
    tableRows() {
      // Merge booked and available slots and sort by time
      const booked = this.combinedSessions.map(slot => ({
        prayer: slot.prayer,
        studentName: slot.studentName,
        time: slot.time,
        studentId: slot.studentId,
        sessionNumber: slot.dailyTrackerId,
        dailyTrackerId: slot.dailyTrackerId,
        isCancelled: slot.isCancelled,
        replacementStudentName: slot.replacementStudentName,
        isAvailable: false
      }));
      
      // Debug logging for available slots
      console.log('Mapping available slots:', this.availableSessionTimes);
      
      const available = this.availableSessionTimes.map(slot => {
        console.log('Processing slot:', slot);
        console.log('slot.startEn:', slot.startEn);
        console.log('slot.endEn:', slot.endEn);
        
        // Validate that we have the correct start time
        const timeToDisplay = slot.startEn || '00:00';
        console.log('Time to display:', timeToDisplay);
        
        // Additional validation - ensure we're not accidentally using end time
        if (slot.startEn === slot.endEn) {
          console.warn('⚠️ WARNING: startEn equals endEn for slot:', slot);
        }
        
        // Verify the time format
        if (timeToDisplay && !timeToDisplay.includes(':')) {
          console.error('❌ ERROR: Invalid time format:', timeToDisplay);
        }
        
        return {
          prayer: slot.prayerName,
          studentName: 'جلسة متاحة',
          time: timeToDisplay,
          isAvailable: true
        };
      });
      
      const rows = [...booked, ...available];
      return rows.sort((a, b) => {
        const [aH, aM] = a.time.split(':').map(Number);
        const [bH, bM] = b.time.split(':').map(Number);
        return aH - bH || aM - bM;
      });
    }
  }
};
</script>

<style scoped>
.session-report {
  direction: rtl;
  text-align: right;
  /* max-height: 800px; */
  /* overflow-y: auto; */
}

.session-block {
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 2px solid #f3f4f6;
  transition: all 0.3s ease;
}

.session-block:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 12px rgba(201, 160, 108, 0.1);
}

.prayer-block {
  margin-bottom: 20px;
}

.time-slots {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.time-slot-container {
  display: flex;
  flex-direction: column;
}

.time-slot {
  background-color: #f8f9fa;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;
  flex-direction: column;
}

.time-slot:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(201, 160, 108, 0.15);
  border-color: #c9a06c;
}

.time {
  font-size: 1.1rem;
}

.student {
  color: #374151;
}

h1, h2, h3, h4 {
  font-family: 'Tajawal', sans-serif;
}

.text-primary {
  color: #c9a06c;
}

/* Custom scrollbar */
.session-report {
  /* max-height: 800px; */
  /* overflow-y: auto; */
}

.session-report::-webkit-scrollbar {
  width: 8px;
}

.session-report::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.session-report::-webkit-scrollbar-thumb {
  background: #c9a06c;
  border-radius: 4px;
}

.session-report::-webkit-scrollbar-thumb:hover {
  background: #b08d5d;
}

/* PDF Export Styles */
.pdf-export {
  background: white !important;
  color: initial !important;
  overflow: visible !important;
}

.pdf-export::-webkit-scrollbar {
  display: none !important;
}

/* Hide edit/delete actions in PDF */
.pdf-export .actions-col {
  display: none !important;
}

/* Hide PDF header on screen by default */
.pdf-header {
  display: none;
}

/* Show PDF header when exporting */
.pdf-export .pdf-header {
  display: block !important;
}

.slot-actions {
  margin-top: 8px;
}

.slot-actions button {
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
}

.slot-actions button:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* PDF Header Styles */
.pdf-header {
  margin-bottom: 0;
  direction: rtl;
  text-align: center;
  padding: 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 2px solid #c9a06c;
  border-radius: 12px;
  box-shadow: 0 8px 16px rgba(201, 160, 108, 0.1);
}
.header-top {
  margin-bottom: 25px;
}
.header-logos {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  height: 100px;
  padding: 0 20px;
}
.logo-left img, .logo-right img {
  max-height: 100px;
  max-width: 150px;
  height: auto;
  width: auto;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}
.logo-placeholder {
  height: 100px;
  width: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #c9a06c 0%, #b8956a 100%);
  border-radius: 8px;
  color: white;
  font-size: 30px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}
.placeholder-text {
  font-size: 24px;
  font-weight: bold;
  margin-top: 5px;
}
.header-divider {
  height: 3px;
  background: linear-gradient(90deg, #c9a06c 0%, #b8956a 50%, #c9a06c 100%);
  border-radius: 2px;
  margin: 0 20px;
}
.header-content {
  text-align: center;
}
.report-title {
  font-size: 36px;
  font-weight: bold;
  color: #c9a06c;
  margin-bottom: 20px;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.1);
}
.report-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
}
.info-row {
  display: flex;
  align-items: center;
  gap: 15px;
  background: rgba(201, 160, 108, 0.1);
  padding: 8px 20px;
  border-radius: 20px;
  border: 1px solid rgba(201, 160, 108, 0.3);
}
.info-label {
  font-size: 18px;
  font-weight: bold;
  color: #c9a06c;
  min-width: 60px;
}
.info-value {
  font-size: 18px;
  color: #333;
  direction: rtl;
  unicode-bidi: bidi-override;
}
/* Reveal PDF header */
.pdf-export .pdf-header {
  margin-bottom: 0 !important;
  width: 100% !important;
}

/* Hide on-screen header in PDF export */
.pdf-export .screen-header {
  display: none !important;
}

/* Allow multi-page PDF breaks */
.page-break-before { page-break-before: always; }
.page-break-after  { page-break-after: always; }

/* Header container flex layout */
.header-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
}
.header-center {
  flex: 1;
  text-align: center;
}
.logo-left, .logo-right {
  flex: 0 0 auto;
}

table, tr, td, th {
  page-break-inside: avoid !important;
  break-inside: avoid !important;
}

.session-report, .session-report * {
  page-break-inside: avoid !important;
  break-inside: avoid !important;
}
</style> 